import { useEffect, useState } from "react";

import { parseDetail } from "../utils/errors";
import CopyableGuid from "../components/CopyableGuid";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AppsIcon from "@mui/icons-material/Apps";
import EditIcon from "@mui/icons-material/Edit";
import useRequests from "../hooks/http";
import { useTenant } from "../hooks/useTenant";

export default function TenantDetail() {
  const { id } = useParams<{ id: string }>();
  const { get, post, patch, del } = useRequests();
  const navigate = useNavigate();
  const { updateTenant, removeTenant } = useTenant();

  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [applications, setApplications] = useState<Application[]>([]);
  const [users, setUsers] = useState<TenantMember[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);

  const [createAppOpen, setCreateAppOpen] = useState(false);
  const [newApp, setNewApp] = useState({ name: "", identifier: "", description: "" });
  const [error, setError] = useState("");

  const [settingsForm, setSettingsForm] = useState({ name: "", slug: "" });
  const [settingsError, setSettingsError] = useState("");
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [deactivateOpen, setDeactivateOpen] = useState(false);

  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [roleForm, setRoleForm] = useState({ name: "", description: "" });
  const [roleError, setRoleError] = useState("");
  const [deactivateRoleTarget, setDeactivateRoleTarget] = useState<Role | null>(null);

  useEffect(() => {
    Promise.all([
      get(`/admin/tenants/${id}`),
      get(`/admin/tenants/${id}/applications`),
      get(`/admin/tenants/${id}/users`),
      get(`/admin/tenants/${id}/roles`),
    ]).then(([tenantRes, appsRes, usersRes, rolesRes]) => {
      if (tenantRes.status === 200) {
        setTenant(tenantRes.data);
        setSettingsForm({ name: tenantRes.data.name, slug: tenantRes.data.slug });
      } else if (tenantRes.status !== 200) {
        setError(parseDetail(tenantRes.data?.detail, "Failed to load tenant"));
      }
      if (appsRes.status === 200 && Array.isArray(appsRes.data)) setApplications(appsRes.data);
      if (usersRes.status === 200 && Array.isArray(usersRes.data)) setUsers(usersRes.data);
      if (rolesRes.status === 200 && Array.isArray(rolesRes.data)) setRoles(rolesRes.data);
      setLoading(false);
    });
  }, [id, get]);

  const handleSaveSettings = async () => {
    setSettingsError("");
    setSettingsSaving(true);
    const { data, status } = await patch(`/admin/tenants/${id}`, settingsForm);
    setSettingsSaving(false);
    if (status === 200) {
      setTenant(data);
      updateTenant(data);
    } else {
      setSettingsError(parseDetail(data?.detail, "Failed to save settings"));
    }
  };

  const handleDeactivate = async () => {
    const { data, status } = await del(`/admin/tenants/${id}`);
    if (status === 200) {
      removeTenant(Number(id));
      navigate("/app/tenants");
    } else {
      setSettingsError(parseDetail(data?.detail, "Failed to deactivate tenant"));
      setDeactivateOpen(false);
    }
  };

  const handleCreateApp = async () => {
    setError("");
    const { data, status } = await post(`/admin/tenants/${id}/applications`, null, newApp);
    if (status === 201 || status === 200) {
      setApplications((prev) => [...prev, data]);
      setCreateAppOpen(false);
      setNewApp({ name: "", identifier: "", description: "" });
    } else {
      setError(parseDetail(data?.detail, "Failed to create application"));
    }
  };

  const openCreateRole = () => {
    setEditingRole(null);
    setRoleForm({ name: "", description: "" });
    setRoleError("");
    setRoleDialogOpen(true);
  };

  const openEditRole = (role: Role) => {
    setEditingRole(role);
    setRoleForm({ name: role.name, description: role.description });
    setRoleError("");
    setRoleDialogOpen(true);
  };

  const handleSaveRole = async () => {
    setRoleError("");
    if (editingRole) {
      const { data, status } = await patch(`/admin/roles/${editingRole.id}`, roleForm);
      if (status === 200) {
        setRoles((prev) => prev.map((r) => (r.id === editingRole.id ? data : r)));
        setRoleDialogOpen(false);
      } else {
        setRoleError(parseDetail(data?.detail, "Failed to update role"));
      }
    } else {
      const { data, status } = await post(`/admin/tenants/${id}/roles`, null, roleForm);
      if (status === 201 || status === 200) {
        setRoles((prev) => [...prev, data]);
        setRoleDialogOpen(false);
      } else {
        setRoleError(parseDetail(data?.detail, "Failed to create role"));
      }
    }
  };

  const handleDeactivateRole = async () => {
    if (!deactivateRoleTarget) return;
    const { status } = await del(`/admin/roles/${deactivateRoleTarget.id}`);
    if (status === 200 || status === 204) {
      setRoles((prev) => prev.map((r) => (r.id === deactivateRoleTarget.id ? { ...r, active: false } : r)));
      setDeactivateRoleTarget(null);
    }
  };

  if (loading) return <LinearProgress />;
  if (!tenant) return <Alert severity="error">Tenant not found</Alert>;

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Box>
          <Typography variant="h4">{tenant.name}</Typography>
          <Typography variant="body2" color="text.secondary">
            {tenant.slug}
          </Typography>
        </Box>
        <Chip label={tenant.active ? "Active" : "Inactive"} color={tenant.active ? "success" : "default"} />
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
        <Tab label={`Applications (${applications.length})`} />
        <Tab label={`Members (${users.length})`} />
        <Tab label={`Roles (${roles.length})`} />
        <Tab label="Settings" />
      </Tabs>

      {tab === 0 && (
        <Box>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
            <Button variant="contained" onClick={() => setCreateAppOpen(true)}>
              New Application
            </Button>
          </Box>
          <Grid container spacing={2}>
            {applications.map((app) => (
              <Grid item xs={12} sm={6} md={4} key={app.id}>
                <Card variant="outlined">
                  <CardActionArea onClick={() => navigate(`/app/applications/${app.id}`)}>
                    <CardContent>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                        <AppsIcon color="primary" />
                        <Typography variant="h6">{app.name}</Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {app.identifier}
                      </Typography>
                      <Typography variant="body2">{app.description}</Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>
          {applications.length === 0 && (
            <Typography color="text.secondary">No applications registered in this tenant yet.</Typography>
          )}
        </Box>
      )}

      {tab === 1 && (
        <>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
            <Button variant="contained" onClick={() => navigate(`/app/tenants/${id}/users`)}>
              Manage Users
            </Button>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Username</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Joined</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.id}>
                    <TableCell>
                      {u.first_name} {u.last_name}
                    </TableCell>
                    <TableCell>{u.username}</TableCell>
                    <TableCell>
                      <Chip size="small" label={u.role || "member"} />
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={u.active ? "Active" : "Inactive"}
                        color={u.active ? "success" : "default"}
                      />
                    </TableCell>
                    <TableCell>{new Date(u.created).toLocaleDateString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {users.length === 0 && (
              <Typography color="text.secondary" sx={{ p: 2 }}>
                No members in this tenant yet.
              </Typography>
            )}
          </TableContainer>
        </>
      )}

      {tab === 2 && (
        <Box>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
            <Button variant="contained" onClick={openCreateRole}>
              New Role
            </Button>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {roles.map((role) => (
                  <TableRow key={role.id}>
                    <TableCell>{role.name}</TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {role.description || "—"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={role.active ? "Active" : "Inactive"}
                        color={role.active ? "success" : "default"}
                      />
                    </TableCell>
                    <TableCell>
                      {role.active && (
                        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => openEditRole(role)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Button size="small" color="error" onClick={() => setDeactivateRoleTarget(role)}>
                            Deactivate
                          </Button>
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {roles.length === 0 && (
              <Typography color="text.secondary" sx={{ p: 2 }}>
                No roles defined for this tenant yet.
              </Typography>
            )}
          </TableContainer>
        </Box>
      )}

      {tab === 3 && (
        <Box sx={{ maxWidth: 560 }}>
          <Card variant="outlined" sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              General
            </Typography>
            {settingsError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {settingsError}
              </Alert>
            )}
            <TextField
              label="Name"
              fullWidth
              margin="normal"
              value={settingsForm.name}
              onChange={(e) => setSettingsForm({ ...settingsForm, name: e.target.value })}
            />
            <TextField
              label="Slug"
              fullWidth
              margin="normal"
              helperText="Unique URL-friendly identifier"
              value={settingsForm.slug}
              onChange={(e) => setSettingsForm({ ...settingsForm, slug: e.target.value })}
            />
            <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
              <Button
                variant="contained"
                onClick={handleSaveSettings}
                disabled={settingsSaving || !settingsForm.name || !settingsForm.slug}
              >
                Save
              </Button>
            </Box>
            <Divider sx={{ my: 3 }} />
            <CopyableGuid label="GUID" value={tenant.guid} />
            <Typography variant="body2" color="text.secondary">
              Created: {new Date(tenant.created).toLocaleString()}
            </Typography>
          </Card>

          <Card variant="outlined" sx={{ p: 3, borderColor: "error.main" }}>
            <Typography variant="h6" color="error" gutterBottom>
              Danger Zone
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Deactivating this tenant will disable all access. This action cannot be undone from
              the UI.
            </Typography>
            <Button variant="outlined" color="error" onClick={() => setDeactivateOpen(true)}>
              Deactivate Tenant
            </Button>
          </Card>

          <Dialog open={deactivateOpen} onClose={() => setDeactivateOpen(false)}>
            <DialogTitle>Deactivate {tenant.name}?</DialogTitle>
            <DialogContent>
              <Typography>
                This will deactivate the tenant and all associated access. Are you sure?
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDeactivateOpen(false)}>Cancel</Button>
              <Button color="error" variant="contained" onClick={handleDeactivate}>
                Deactivate
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      )}

      {/* Create / Edit Role Dialog */}
      <Dialog open={roleDialogOpen} onClose={() => setRoleDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingRole ? "Edit Role" : "New Role"}</DialogTitle>
        <DialogContent>
          {roleError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {roleError}
            </Alert>
          )}
          <TextField
            label="Name"
            fullWidth
            margin="normal"
            value={roleForm.name}
            onChange={(e) => setRoleForm({ ...roleForm, name: e.target.value })}
          />
          <TextField
            label="Description"
            fullWidth
            margin="normal"
            multiline
            rows={2}
            value={roleForm.description}
            onChange={(e) => setRoleForm({ ...roleForm, description: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRoleDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveRole} disabled={!roleForm.name}>
            {editingRole ? "Save" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Deactivate Role Confirmation */}
      <Dialog open={Boolean(deactivateRoleTarget)} onClose={() => setDeactivateRoleTarget(null)}>
        <DialogTitle>Deactivate Role</DialogTitle>
        <DialogContent>
          <Typography>
            Deactivate <strong>{deactivateRoleTarget?.name}</strong>? Users assigned this role will
            be unaffected but the role cannot be assigned to new users.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeactivateRoleTarget(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDeactivateRole}>
            Deactivate
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Application Dialog */}
      <Dialog open={createAppOpen} onClose={() => setCreateAppOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New Application</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <TextField
            label="Name"
            fullWidth
            margin="normal"
            value={newApp.name}
            onChange={(e) => setNewApp({ ...newApp, name: e.target.value })}
          />
          <TextField
            label="Identifier"
            fullWidth
            margin="normal"
            placeholder="e.g. myapp.example.com"
            helperText="Domain or service identifier, used as JWT audience claim"
            value={newApp.identifier}
            onChange={(e) => setNewApp({ ...newApp, identifier: e.target.value })}
          />
          <TextField
            label="Description"
            fullWidth
            margin="normal"
            multiline
            rows={2}
            value={newApp.description}
            onChange={(e) => setNewApp({ ...newApp, description: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateAppOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateApp}
            disabled={!newApp.name || !newApp.identifier}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
